import { createPortal } from "react-dom";
import Photos from "./Photos";
import { uploadGuidePhotos } from "./photo_state";
import { useCallback, useMemo, useContext, useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import { Image } from "uikit-react";
import { InputContainer } from "uikit-react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { AppContexts } from "../../../providers";
import { Form, FormRow, FormInputWrapper, Link } from "../../../ui";
import Tags from "../../common/Tags";
import MasterConfirm from "../../common/Confirm";
import MacrosReservationKeys from "../../common/MacroReservationKeys";
import VisibilityRules from "../../common/VisibilityRules";
import GuideContentAI from "../../common/GuideContentAI";
import {
  mergeCreatedGuideSectionOrder,
  upsertGuideSection,
} from "./section_state";

const schema = yup.object().shape({
  title: yup.string().required(),
});

const defaultValues = {
  title: "",
  body: "",
  tags: [],
  attachment: "",
  video: "",
  icon: null,
  thumbnail: null,
};

export const MODAL_NAME = "master-property-guide-add";

const PropertyGuideAdd = ({ onSaved, onSectionSaved }) => {
  const { setUser } = useContext(AppContexts.AuthContext);
  const { uploadFile, genUUID } = useContext(AppContexts.UtilContext);
  const { get, settings, setSettings } = useContext(
    AppContexts.SettingsContext,
  );
  const { createSection, selectedTab } = useContext(AppContexts.RentalsContext);

  const [photos, setPhotos] = useState([]);
  const [photoActionsTarget, setPhotoActionsTarget] = useState(null);
  const [attachPDF, setAttachPDF] = useState({});
  const [loading, setLoading] = useState(false);
  const [restrictions, setRestrictions] = useState([]);
  const [isGuideAiActive, setIsGuideAiActive] = useState(false);
  const offcanvasRef = useRef(null);
  const savingRef = useRef(false);
  const uploadedPhotos = useRef(new Map());

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleRestrictionsChange = (restrictions) => {
    setRestrictions(restrictions);
  }

  useEffect(() => {
    clearForms();
  }, []);

  useEffect(() => {
    const element = offcanvasRef.current;
    const preventCloseDuringSave = (event) => {
      if (event.target === element && savingRef.current) event.preventDefault();
    };
    element.addEventListener("beforehide", preventCloseDuringSave);
    return () => {
      element.removeEventListener("beforehide", preventCloseDuringSave);
      if (element?.classList.contains("uk-open")) UIkit.offcanvas(element).hide();
      document.body.style.overflow = "";
    };
  }, []);

  const clearForms = useCallback(() => {
    Object.keys(defaultValues).forEach((formname) => {
      setValue(formname, defaultValues[formname]);
    });
    setPhotos([]);
    uploadedPhotos.current.clear();
    setAttachPDF({});
    setRestrictions([]);
  }, []);

  const handleRegister = async (formdata) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setLoading(true);
    let attachment_url;
    try {
      const attachments_buf = await uploadGuidePhotos(photos, uploadFile, genUUID, uploadedPhotos.current);
      if (attachPDF?.file) {
        attachment_url = await uploadFile(
          attachPDF.file,
          `workorders/attachments/${genUUID()}`,
        );

        if (!attachment_url) {
          UIkit.notification("Failed Uploading PDF", "error");
          return;
        }
      }

      if (!!(attachPDF?.name || attachPDF?.original_name)) {
        if (attachPDF?.file) {
          attachments_buf.push({
            ...attachPDF,
            name: formdata.attachment || attachPDF.original_name || attachPDF.name,
            file: undefined,
            url: attachment_url,
          });
        } else if (attachPDF?.url) {
          attachments_buf.push({
            ...attachPDF,
            file: undefined,
            name: formdata.attachment || attachPDF.original_name || attachPDF.name,
          });
        }
      }

      if (formdata.video) {
        attachments_buf.push({
          type: "you_tube",
          name: "",
          original_name: "",
          url: formdata.video,
        });
      }

      formdata.attachments = attachments_buf;
      formdata.restrictions = restrictions;

      let orders = settings?.welcome_order;
      if (!orders) {
        const loadedSettings = await get(["welcome_order"]);
        orders = loadedSettings?.welcome_order;
      }

      const selectedOrder = (orders || []).find(
        (order) => order.type === selectedTab?.type,
      );
      if (!selectedTab || !selectedOrder) {
        UIkit.notification("Select a Home Guide section before adding a block", "error");
        return;
      }

      setLoading(true);
      let [section, sectionErr] = await createSection({
        ...formdata,
        welcome_order_type: selectedTab.type,
      });
      const mediaConfirmed = Array.isArray(section?.attachments) && attachments_buf.every((expected) =>
        section.attachments.some((attachment) => attachment.type === expected.type && attachment.url === expected.url));
      if (sectionErr !== null || section === null || !mediaConfirmed) {
        UIkit.notification("Failed", "error");
        setLoading(false);
      } else {
        const { welcome_order: persistedOrder, ...savedSection } = section;
        const nextOrders = mergeCreatedGuideSectionOrder(
          orders,
          selectedTab.type,
          savedSection.welcome_guide_question_id,
          persistedOrder,
        );
        setUser((current) => current ? {
          ...current,
          welcome_guide: upsertGuideSection(
            current.welcome_guide,
            savedSection,
          ),
        } : current);
        onSectionSaved?.(savedSection, nextOrders);
        setSettings((current) => ({
          ...current,
          welcome_order: nextOrders,
        }));

        savingRef.current = false;
        UIkit.offcanvas(offcanvasRef.current).hide();
        UIkit.notification("Added Successfully", "success");
        onSaved?.();

        setLoading(false);
        clearForms();
      }
    } catch (err) {
      console.log("ERROR", err);
      UIkit.notification("Failed", "error");
      setLoading(false);
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (savingRef.current) return;
    clearForms();
    document.body.style.overflow = "";
    UIkit.offcanvas(offcanvasRef.current).hide();
  };

  const deletePDF = () => {
    setValue("attachments", null);
    setAttachPDF({});
  };

  const body = useMemo(() => getValues("body"), [watch("body")]);
  const title = useMemo(() => getValues("title"), [watch("title")]);
  const tags = useMemo(() => getValues("tags"), [watch("tags")]);
  const icon = useMemo(() => getValues("icon"), [watch("icon")]);

  const handleError = (err) => console.error(err);

  const bodyTextareaRef = useRef(null);
  const handleInsertMacro = (macro) => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = (body || "").slice(0, start) + macro + (body || "").slice(end);
    setValue("body", newValue);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + macro.length;
    }, 0);
  };

  return createPortal(
    <>
      <div
        id={MODAL_NAME}
        ref={offcanvasRef}
        data-guide-editor-offcanvas
        className="uk-modal-full hms-modals uk-modal uk-open"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <div className="uk-modal-header guide-block-modal-header">
              <h2 className="uk-modal-title">Add Block</h2>
              <button
                className="uk-offcanvas-close uk-close-large guide-block-modal-close"
                type="button"
                aria-label="Close Add Block"
                uk-close="true"
              ></button>
            </div>
            <div className="uk-modal-body">
              <Form
                className="uk-grid-small"
                data-uk-grid
                onSubmit={handleSubmit(handleRegister, handleError)}
              >
                <FormRow>
                  <FormInputWrapper label="Title*" size="one">
                    <InputContainer>
                      <input
                        className={classnames("uk-input", {
                          error: errors["title"],
                        })}
                        type="text"
                        {...register("title")}
                      />
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>
                <FormRow>
                  <FormInputWrapper label="Tags">
                    <div className="hms-add-tag">
                      <Tags
                        value={tags}
                        onChange={(editedTags) => setValue("tags", editedTags)}
                      />
                    </div>
                  </FormInputWrapper>
                </FormRow>
                <FormRow>
                  <FormInputWrapper label="Text" size="two">
                    <span className="hms-align-right">
                      <Link className="m-l-10">
                        <a
                          href="https://intercom.help/ruebarueapp/en/articles/9792129-style-shortcuts"
                          target="_blank"
                        >
                          View Styles
                        </a>
                      </Link>
                    </span>
                    <InputContainer>
                      <textarea
                        ref={bodyTextareaRef}
                        className={classnames("uk-textarea", {
                          error: errors.body,
                        })}
                        value={body || ""}
                        onChange={(event) =>
                          setValue("body", event.target.value)
                        }
                        rows="7"
                      />
                      <div className="large-msg-typing-btn">
                        <div className="uk-flex uk-flex-middle uk-flex-wrap sms-composer-actions">
                          <div hidden={isGuideAiActive}>
                            <MacrosReservationKeys
                              onMacroClick={handleInsertMacro}
                              portal
                            />
                          </div>
                          <GuideContentAI
                            promptId="guide-content-ai-add-prompt"
                            title={title || ""}
                            value={body || ""}
                            onChange={(content) => setValue("body", content)}
                            onActiveChange={setIsGuideAiActive}
                            photos={photos}
                            photoActionsTarget={photoActionsTarget}
                            preparePhotos={(selected) => uploadGuidePhotos(selected, uploadFile, genUUID, uploadedPhotos.current)}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>
                <Photos photos={photos} onChange={setPhotos} disabled={loading || isGuideAiActive} />
                <div ref={setPhotoActionsTarget} className="guide-photo-ai uk-width-1-1" style={{ marginTop: 0, marginBottom: photos.length ? 20 : 0 }} />
                <FormRow>
                  <FormInputWrapper label="Video" size="two">
                    <InputContainer>
                      <input
                        className={classnames("uk-input", {
                          error: errors.video,
                        })}
                        type="text"
                        {...register("video")}
                        placeholder="YouTube or Vimeo Embed Link"
                      />
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>
                <FormRow>
                  <FormInputWrapper label="PDF Label" size="one">
                    <InputContainer>
                      <input
                        className={classnames("uk-input", {
                          error: errors.label,
                        })}
                        type="text"
                        {...register("attachment")}
                      />
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>
                <FormRow>
                  <FormInputWrapper label="PDF">
                    <div className="uk-form-custom">
                      {!!(attachPDF.name || attachPDF.original_name) ? (
                        <>
                          <input
                            key="pdf_key"
                            type="file"
                            {...register("attachments")}
                            onChange={(evt) =>
                              setAttachPDF({
                                file: evt.target.files[0] || null,
                                name: evt.target.files[0]?.name,
                                original_name: evt.target.files[0]?.name,
                                type: "file",
                                url: "",
                              })
                            }
                          />
                          <span className="grey-text">
                            {attachPDF?.name || attachPDF?.original_name}
                          </span>
                        </>
                      ) : (
                        <>
                          <input
                            key="pdf_key"
                            type="file"
                            {...register("attachments")}
                            onChange={(evt) =>
                              setAttachPDF({
                                file: evt.target.files[0] || null,
                                name: evt.target.files[0]?.name,
                                original_name: evt.target.files[0]?.name,
                                type: "file",
                                url: "",
                              })
                            }
                          />
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <Image
                              src="/images/upload-pdf.svg"
                              className="hms-navbar"
                            />
                          </button>
                        </>
                      )}
                    </div>
                    {!!(attachPDF?.name || attachPDF?.original_name) && (
                      <MasterConfirm onDelete={deletePDF} />
                    )}
                  </FormInputWrapper>
                </FormRow>
                <FormRow>
                  <VisibilityRules
                    restrictions={restrictions}
                    onChangeBlockSettings={handleRestrictionsChange}
                    itemType={'welcome_guide_question'}
                  />
                </FormRow>

                <div className="uk-width-1-1 uk-modal-footer uk-text-right guide-block-modal-actions">
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    type="submit"
                    disabled={loading || isGuideAiActive}
                  >
                    {loading ? (
                      <div data-uk-spinner="ratio: 0.5;"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    className="uk-button hms-btn"
                    onClick={handleClose}
                    disabled={loading}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default PropertyGuideAdd;
